import { Close, Edit } from '@mui/icons-material'
import { Box, Button, FormControl, IconButton, InputLabel, List, ListItem, ListItemText, MenuItem, Select, TextField } from '@mui/material'
import React, { useEffect } from 'react'
import { Colors } from '../../../../styles/theme'
import { ValidDataService } from '../../../../service/validDataService'
import useAbortController from '../../../../hooks/useAbortController'
import { isLocalDev } from '../../../../utils/environment'
import { toast } from 'react-toastify'
import useDeleteRecord from '../../../../hooks/useDeleteRecord'
import ConfirmationDialog from '../../../ui/confirmationDialog'
import useDialogModal from '../../../../hooks/useDialogModal'

export const supplierTypes = [{name: 'frames', display: 'Frames'}, {name: 'contacts', display: 'Contact Lens'}, {name: 'lens', display: 'Lens'}, {name: 'accessories', display: 'Accessories'}];

function SupplierBrandList({ state, updateState, supplierId }) {

    const [signal, abortRequest] = useAbortController();
    const [
        ConfirmationDialogComp,
        showConfirmationDialog,
        closeConfirmationDialog,
    ] = useDialogModal(ConfirmationDialog);

    const { deleteRecords, isDeleting } = useDeleteRecord(showConfirmationDialog, closeConfirmationDialog);

    useEffect(() => {

        (async () => {
            updateState({ loading: true });
            try {
                const resopnse = await ValidDataService.getBrandForSupplierWithLimit(0, 100, '', '', supplierId, signal)
                const { code, data } = resopnse;
                updateState({
                    data: data.data,
                    loading: false
                });

            } catch (error) {
                if (isLocalDev) {
                    console.error(error)
                }
                updateState({ loading: false });
            }
        })()

        return () => {
            abortRequest()
        }
    }, [supplierId, updateState])

    const handleEditBrand = (name) => {
        const index = state.data.findIndex(item => item.name === name);
        if (index !== -1) {
            updateState({ brand: state.data[index].name, editIndex: index, type: state.data[index].type });
        }
    };

    const handleDeleteBrand = (name) => {

        const updatedData = state.data.filter(item => item.name !== name);
        updateState({ data: updatedData });
    };

    const handleAddBrandToSupplier = () => {
        // Check if the brand already exists in the list
        const brandExists = state.data.some((item, index) => item.name.toLowerCase() === state.brand.toLowerCase() && index !== state.editIndex);

        if (brandExists) {
            // Show a toast message if the brand already exists
            toast.info('Brand already exists in the list');
        } else {
            if (state.editIndex !== null) {
                // Update the existing item
                const updatedData = [...state.data];
                updatedData[state.editIndex].name = state.brand;
                updatedData[state.editIndex].type = state.type;
                updateState({ data: updatedData });
                updateState({ editIndex: null });
            } else {
                // Add the brand to the list if it doesn't exist
                updateState({
                    data: [...state.data, { id: null, name: state.brand, type: state.type }]
                });
            }
            updateState({ brand: '' });
        }
    };

    const deleteRecordHandler = async (rowids) => {
        const rowidData = Array.isArray(rowids) ? rowids : [rowids]
        await deleteRecords(rowidData,
            ValidDataService.deleteBrandFromSupplier,
            (data) => {
                // Success callback: Update the state
                const updateList = state.data.filter(d => !rowidData.map(x => parseInt(x, 10)).includes(d.id));
                updateState({ data: updateList });
            },
            (errorMessage) => {
                // Failure callback: Handle the error (e.g., show a different toast)
                console.error("Deletion failed:", errorMessage);
            })
    }

    const handleTypeChange = (event) => {
        //console.log(event.target.value)
        updateState({type: event.target.value})
    }

    return (
        <Box>
            <Box display="flex">
                <FormControl sx={{ minWidth: { xs: '100%', sm: 180 } }} size='small'>
                    <InputLabel id='brand-type-label'>Type</InputLabel>
                    <Select
                        labelId="brand-type-label"
                        id="brand-type"
                        label="Type"
                        sx={{mr: 1}}
                        value={state.type}
                        onChange={handleTypeChange}
                    >
                        {supplierTypes.map((type) => (
                            <MenuItem key={type.name} value={type.name}>
                                {type.display}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
                <TextField
                    value={state.brand}
                    onChange={(event) => updateState({ brand: event.target.value })}
                    name="brand"
                    label="Brand"
                    type="text"
                    size='small'
                />

                <Button
                    type='button'
                    sx={{ ml: 1 }}
                    onClick={handleAddBrandToSupplier}
                    disabled={state.isDeleting || state.brand === ''}
                    variant='outlined'>{state.editIndex !== null ? 'Update Brand' : 'Add to supplier'}</Button>
            </Box>
            {state.loading ? 'Loading brands' :
                <Box>
                    <List dense={true}
                        sx={{
                            maxWidth: { sm: '100%', md: 400 },
                            maxHeight: 300,
                            overflow: 'auto',
                        }}>
                        {state.data.length > 0 && state.data.map((item, index) => (
                            <ListItem
                                key={item.name}
                                sx={{
                                    border: '1px solid #ccc',
                                    borderRadius: '4px',
                                    marginBottom: '8px',
                                    padding: '8px',
                                    bgcolor: item.id > 0 ? Colors.body_bg : 'background.paper',
                                }}
                                secondaryAction={
                                    <>
                                        <IconButton edge="end" aria-label="edit" onClick={() => handleEditBrand(item.name)}>
                                            <Edit sx={{ color: Colors.secondary }} />
                                        </IconButton>
                                        {
                                            item.id ?
                                                <IconButton edge="end" aria-label="delete" onClick={() => deleteRecordHandler(item.id)}>
                                                    <Close sx={{ color: Colors.danger }} />
                                                </IconButton>
                                                :
                                                <IconButton edge="end" aria-label="delete" onClick={() => handleDeleteBrand(item.name)}>
                                                    <Close sx={{ color: Colors.danger }} />
                                                </IconButton>
                                        }
                                    </>
                                }>
                                <ListItemText sx={{textTransform: 'capitalize'}} primary={item.name} secondary={item.type}/>
                            </ListItem>
                        ))}
                    </List>
                </Box>
            }
            <ConfirmationDialogComp
                label={{
                    positive: "Confirm",
                    negative: "Cancel",
                }} />
        </Box>
    )
}

export default SupplierBrandList